import { useRef, useState } from 'react';

import { extractEatonDspFile } from '@/lib/eaton-dsp/extractEatonDsp';
import {
  mergeEatonResults,
  summarizeEatonResults,
} from '@/lib/eaton-dsp/summarizeExtract';
import type { EatonDspFileResult } from '@/lib/eaton-dsp/types';
import { writeExtractedWorkbook } from '@/lib/eaton-dsp/writeExtractedWorkbook';
import { triggerDownload } from 'src/lib/pricelist-utils';

/**
 * Holds dropped Eaton DSP workbooks and their flattened extract. Dropping
 * the same filename again replaces the previous readout.
 */
export function useEatonDsp() {
  const [results, setResults] = useState<EatonDspFileResult[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const generationRef = useRef(0);
  const summary = summarizeEatonResults(results);

  function invalidateInFlight(): void {
    generationRef.current += 1;
    setIsExtracting(false);
  }

  async function addFiles(files: File[]): Promise<void> {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setIsExtracting(true);
    const incoming = await Promise.all(files.map(extractEatonDspFile));
    if (generation !== generationRef.current) return;
    setResults((current) => mergeEatonResults(current, incoming));
    setIsExtracting(false);
  }

  function removeFile(fileName: string): void {
    setResults((current) =>
      current.filter((result) => result.fileName !== fileName),
    );
  }

  function clear(): void {
    invalidateInFlight();
    setResults([]);
  }

  function downloadExtract(): void {
    if (summary.priceCount === 0) return;
    const buffer = writeExtractedWorkbook(summary);
    triggerDownload(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'eaton-dsp-extract.xlsx',
    );
  }

  return {
    results,
    summary,
    isExtracting,
    addFiles,
    removeFile,
    clear,
    downloadExtract,
  };
}
